import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import * as api from '../api/client';

interface ActionModalProps {
  action: string;
  item: {
    id: string;
    recordId: string;
    recordType: string;
    name: string;
    issueType: string;
    issueLabel: string;
    email?: string;
    personalEmail?: string;
    phone?: string;
    hasSource?: boolean;
    hasSectorTag?: boolean;
    hasCity?: boolean;
    hasCountry?: boolean;
    currencyCode?: string;
    countryName?: string;
  };
  consultantId: string;
  onClose: () => void;
  onComplete: () => void;
}

export function ActionModal({ action, item, consultantId, onClose, onComplete }: ActionModalProps) {
  const { getGraphToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Lookup data
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [editEmail, setEditEmail] = useState(item.email || '');
  const [editPhone, setEditPhone] = useState(item.phone || '');
  // Seed first/last name from the candidate's full name (first token / remainder).
  const nameParts = (item.name || '').trim().split(/\s+/);
  const initialFirst = nameParts[0] || '';
  const initialLast = nameParts.slice(1).join(' ');
  const [editFirstName, setEditFirstName] = useState(initialFirst);
  const [editLastName, setEditLastName] = useState(initialLast);
  const [editCity, setEditCity] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [sectors, setSectors] = useState<any[]>([]);
  const [closeReasons, setCloseReasons] = useState<any[]>([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [sectorSearch, setSectorSearch] = useState('');
  const [description, setDescription] = useState('');
  // Currency mismatch: is the currency wrong, or the location?
  const [mismatchFix, setMismatchFix] = useState<'currency' | 'location'>('currency');

  // CV upload
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [cvConfirmed, setCvConfirmed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CV gate: block upload if candidate has no email AND no phone
  const cvBlocked = action === 'upload-cv' && !item.email && !item.personalEmail && !item.phone;

  useEffect(() => {
    // Load lookups based on action type
    if (action === 'fix-currency') {
      api.getLookupCurrencies().then(r => setCurrencies(r.currencies || [])).catch(() => {});
      // A currency mismatch can be fixed by correcting the location instead, so
      // load countries too.
      api.getLookupCountries().then(r => setCountries(r.countries || [])).catch(() => {});
    }
    if (action === 'fix-address') api.getLookupCountries().then(r => setCountries(r.countries || [])).catch(() => {});
    if (action === 'add-tags') {
      api.getLookupSources().then(r => setSources(r.sources || [])).catch(() => {});
      api.getLookupSectors().then(r => setSectors(r.sectors || [])).catch(() => {});
      api.getLookupCountries().then(r => setCountries(r.countries || [])).catch(() => {});
    }
    if (action === 'close-vacancy') api.getLookupCloseReasons().then(r => setCloseReasons(r.reasons || [])).catch(() => {});
  }, [action]);

  const handleFileSelect = useCallback(async (file: File) => {
    setFileName(file.name);
    setLoading(true);
    setError('');
    try {
      const graphToken = await getGraphToken();
      // An empty token means MSAL couldn't authorise Mail.Send silently. Say so
      // rather than posting without it and failing with a cryptic error.
      if (!graphToken) {
        setError("Couldn't get permission to send email. Please sign out and sign in again, then retry.");
        setLoading(false);
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        try {
          // Pass the candidate so the upload is linked to them — without it the
          // audit trail can't tell which record a CV was meant for, and DaXtra
          // filing it against a duplicate is undetectable.
          const result = await api.actionUploadCv(file.name, base64, graphToken, item.recordId);
          setSuccess(result.message);
          setTimeout(() => { onComplete(); }, 1500);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }, [getGraphToken, onComplete]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      let result;
      switch (action) {
        case 'log-call':
          result = await api.actionLogCall(item.recordId, item.name, description, consultantId);
          break;
        case 'close-vacancy':
          if (!selectedValue) { setError('Select a close reason'); setLoading(false); return; }
          result = await api.actionCloseVacancy(item.recordId, parseInt(selectedValue), item.name);
          break;
        case 'fix-currency':
          if (mismatchFix === 'location') {
            // The location is wrong — correct the country instead of the currency.
            if (!selectedCountryId) { setError('Select the correct country'); setLoading(false); return; }
            result = await api.actionFixAddress(item.recordId, selectedCountryId, item.name);
          } else {
            if (!selectedValue) { setError('Select the correct currency'); setLoading(false); return; }
            result = await api.actionFixCurrency(item.recordId, selectedValue, item.name);
          }
          break;
        case 'fix-address':
          if (!selectedValue) { setError('Select a country'); setLoading(false); return; }
          result = await api.actionFixAddress(item.recordId, selectedValue, item.name);
          break;
        case 'fix-solution':
          if (!selectedValue) { setError('Select a solution'); setLoading(false); return; }
          result = await api.actionFixSolution(item.recordId, selectedValue, item.name);
          break;
        case 'edit-contact':
          result = await api.actionUpdateContact(
            item.recordId,
            editEmail || undefined,
            editPhone || undefined,
            // Only send names if the user actually changed them, so we never
            // overwrite the stored name with a naive split of the full name.
            editFirstName !== initialFirst ? editFirstName : undefined,
            editLastName !== initialLast ? editLastName : undefined,
            item.name
          );
          break;
        case 'add-tags':
          if (!selectedValue && selectedSectors.length === 0 && !editCity && !selectedCountryId) {
            setError('Fill in at least one field'); setLoading(false); return;
          }
          result = await api.actionAddTags(
            item.recordId,
            selectedValue || undefined,
            selectedSectors.length > 0 ? selectedSectors.join(', ') : undefined,
            editCity || undefined,
            selectedCountryId || undefined,
            item.name
          );
          break;
        case 'still-open':
        case 'correct-as-is':
          result = await api.actionAcknowledge(consultantId, item.recordId, item.issueType.replace(/-/g, '_'));
          break;
        case 'dismiss':
          result = await api.actionDismiss(consultantId, item.recordId, item.issueType.replace(/-/g, '_'), description || undefined);
          break;
        default:
          setError('Unknown action');
          setLoading(false);
          return;
      }
      setSuccess(result.message);
      setTimeout(() => { onComplete(); }, 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    background: 'var(--bg-page)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-card)',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '13px',
    marginBottom: '12px',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: '440px', maxHeight: '80vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>{actionTitle(action)}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{item.name}</div>
          </div>
          <button className="btn btn-secondary" onClick={onClose} style={{ padding: '4px 10px' }}>✕</button>
        </div>

        {success ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--state-positive)' }}>
            ✓ {success}
          </div>
        ) : (
          <>
            {/* Action-specific content */}
            {action === 'log-call' && (
              <div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Creates a phone call with "Candidate Ad-Response Call" purpose on {item.name}'s record.
                </p>
                <textarea
                  placeholder="Call notes / description..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    background: 'var(--bg-page)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-card)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    fontFamily: 'var(--font-family)',
                    resize: 'vertical',
                    marginBottom: '12px',
                  }}
                />
              </div>
            )}

            {action === 'close-vacancy' && (
              <select style={selectStyle} value={selectedValue} onChange={e => setSelectedValue(e.target.value)}>
                <option value="">Select close reason...</option>
                {closeReasons.map((r: any) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            )}

            {action === 'fix-currency' && (
              <div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  This vacancy is set to <strong style={{ color: 'var(--text-primary)' }}>{item.currencyCode || 'a currency'}</strong>
                  {' '}but the location is <strong style={{ color: 'var(--text-primary)' }}>{item.countryName || 'somewhere else'}</strong>.
                  Which one is wrong?
                </p>

                {/* Choose what to correct */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                  <button
                    type="button"
                    className={`btn ${mismatchFix === 'currency' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ flex: 1, padding: '10px' }}
                    onClick={() => setMismatchFix('currency')}
                  >
                    The currency is wrong
                  </button>
                  <button
                    type="button"
                    className={`btn ${mismatchFix === 'location' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ flex: 1, padding: '10px' }}
                    onClick={() => setMismatchFix('location')}
                  >
                    The location is wrong
                  </button>
                </div>

                {mismatchFix === 'currency' ? (
                  <>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Correct currency</div>
                    <select style={selectStyle} value={selectedValue} onChange={e => setSelectedValue(e.target.value)}>
                      <option value="">Select currency...</option>
                      {currencies.map((c: any) => (
                        <option key={c.transactioncurrencyid} value={c.transactioncurrencyid}>{c.isocurrencycode} — {c.currencyname}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Correct location</div>
                    <select style={selectStyle} value={selectedCountryId} onChange={e => setSelectedCountryId(e.target.value)}>
                      <option value="">Select country...</option>
                      {countries.map((c: any) => (
                        <option key={c.mercury_countryid} value={c.mercury_countryid}>{c.mercury_name}</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}

            {action === 'fix-address' && (
              <select style={selectStyle} value={selectedValue} onChange={e => setSelectedValue(e.target.value)}>
                <option value="">Select country...</option>
                {countries.map((c: any) => (
                  <option key={c.mercury_countryid} value={c.mercury_countryid}>{c.mercury_name}</option>
                ))}
              </select>
            )}

            {action === 'edit-contact' && (
              <div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>First Name</div>
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={e => setEditFirstName(e.target.value)}
                      placeholder="First name"
                      style={selectStyle}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Last Name</div>
                    <input
                      type="text"
                      value={editLastName}
                      onChange={e => setEditLastName(e.target.value)}
                      placeholder="Last name"
                      style={selectStyle}
                    />
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Personal Email</div>
                <input
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                  placeholder="email@example.com"
                  style={selectStyle}
                />
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Personal Phone</div>
                <input
                  type="tel"
                  value={editPhone}
                  onChange={e => setEditPhone(e.target.value)}
                  placeholder="+44..."
                  style={selectStyle}
                />
              </div>
            )}

            {action === 'fix-solution' && (
              <div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Select the solution for this vacancy:
                </p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  {[
                    { id: 'f2a39ab7-5054-f111-bec7-7ced8d6b281b', label: 'Connect' },
                    { id: '5cd00004-ea15-f011-998a-00224840e995', label: 'Consult' },
                    { id: '0b1833f1-e915-f011-998a-00224840e995', label: 'Deploy' },
                  ].map(s => (
                    <button
                      key={s.id}
                      className={`btn ${selectedValue === s.id ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ flex: 1, padding: '10px' }}
                      onClick={() => setSelectedValue(s.id)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {action === 'add-tags' && (
              <div>
                {!item.hasSource && (
                  <>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Source</div>
                    <select style={selectStyle} value={selectedValue} onChange={e => setSelectedValue(e.target.value)}>
                      <option value="">Select source...</option>
                      {sources.map((s: any) => (
                        <option key={s.crimson_candidatesourceid} value={s.crimson_candidatesourceid}>{s.crimson_name}</option>
                      ))}
                    </select>
                  </>
                )}
                {!item.hasSectorTag && (
                  <>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Sectors</div>
                    {/* Selected sectors as pills */}
                    {selectedSectors.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                        {selectedSectors.map(s => (
                          <span
                            key={s}
                            className="pill pill-teal"
                            style={{ cursor: 'pointer', padding: '4px 8px', fontSize: '12px' }}
                            onClick={() => setSelectedSectors(prev => prev.filter(x => x !== s))}
                          >
                            {s} ✕
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Search input */}
                    <input
                      type="text"
                      placeholder="Search sectors..."
                      value={sectorSearch}
                      onChange={e => setSectorSearch(e.target.value)}
                      style={{
                        width: '100%',
                        background: 'var(--bg-page)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-card)',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '13px',
                        marginBottom: '4px',
                      }}
                    />
                    {/* Filtered results */}
                    {sectorSearch.length > 0 && (
                      <div style={{
                        maxHeight: '160px',
                        overflowY: 'auto',
                        background: 'var(--bg-page)',
                        border: '1px solid var(--border-card)',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        {sectors
                          .filter((s: any) =>
                            s.mercury_name.toLowerCase().includes(sectorSearch.toLowerCase()) &&
                            !selectedSectors.includes(s.mercury_name)
                          )
                          .slice(0, 20)
                          .map((s: any) => (
                            <div
                              key={s.mercury_tagid}
                              style={{
                                padding: '8px 12px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                borderBottom: '1px solid var(--border-subtle)',
                              }}
                              onMouseOver={e => (e.currentTarget.style.background = 'rgba(93,143,154,0.1)')}
                              onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                              onClick={() => {
                                setSelectedSectors(prev => [...prev, s.mercury_name]);
                                setSectorSearch('');
                              }}
                            >
                              {s.mercury_name}
                            </div>
                          ))
                        }
                        {sectors.filter((s: any) =>
                          s.mercury_name.toLowerCase().includes(sectorSearch.toLowerCase()) &&
                          !selectedSectors.includes(s.mercury_name)
                        ).length === 0 && (
                          <div style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                            No sectors match "{sectorSearch}"
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {/* City — only show if missing */}
                {!item.hasCity && (
                  <>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', marginTop: '8px' }}>City</div>
                    <input
                      type="text"
                      value={editCity}
                      onChange={e => setEditCity(e.target.value)}
                      placeholder="Enter city..."
                      style={selectStyle}
                    />
                  </>
                )}

                {/* Country — only show if missing */}
                {!item.hasCountry && (
                  <>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Country</div>
                    <select style={selectStyle} value={selectedCountryId} onChange={e => setSelectedCountryId(e.target.value)}>
                      <option value="">Select country...</option>
                      {countries.map((c: any) => (
                        <option key={c.mercury_countryid} value={c.mercury_countryid}>{c.mercury_name}</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}

            {(action === 'still-open' || action === 'correct-as-is') && (
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                This acknowledges the issue for the current period. It will lift your score now but resurface next month.
              </p>
            )}

            {action === 'dismiss' && (
              <div>
                <p style={{ fontSize: '13px', color: 'var(--state-warning)', marginBottom: '12px' }}>
                  Admin override — this permanently removes the item from this consultant's worklist. It will never resurface.
                </p>
                <textarea
                  placeholder="Reason for dismissal (optional)..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    background: 'var(--bg-page)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-card)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    resize: 'vertical',
                    marginBottom: '12px',
                  }}
                />
              </div>
            )}

            {action === 'upload-cv' && (
              <div>
                {cvBlocked ? (
                  /* No email AND no phone — block the upload entirely */
                  <div style={{
                    border: '2px solid var(--state-warning)',
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center',
                    marginBottom: '16px',
                    background: 'rgba(199,121,102,0.08)',
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--state-warning)', marginBottom: '8px' }}>
                      Cannot upload CV
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                      This candidate has no email address or phone number on their record.
                      Uploading a CV without contact details will almost certainly create a duplicate in DaXtra.
                    </p>
                    <p style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                      Please add an email or phone number first using <strong>Edit contact</strong>, then upload the CV.
                    </p>
                  </div>
                ) : (
                  /* Has email and/or phone — show upload with confirmation */
                  <>
                    {/* Duplicate warning */}
                    <div style={{
                      background: 'rgba(199,121,102,0.08)',
                      border: '1px solid var(--state-warning)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                    }}>
                      <strong style={{ color: 'var(--state-warning)' }}>Warning:</strong> DaXtra matches the CV to this record by
                      email. If the address on the CV isn't one of these, it will create a duplicate contact instead.
                      <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
                        <span>Personal:</span>
                        <span>
                          {item.personalEmail
                            ? <strong style={{ color: 'var(--text-primary)' }}>{item.personalEmail}</strong>
                            : <span style={{ color: 'var(--state-warning)', fontWeight: 500 }}>missing — add it to the record first if the CV shows one</span>}
                        </span>
                        <span>Work:</span>
                        <span>{item.email ? <strong style={{ color: 'var(--text-primary)' }}>{item.email}</strong> : '—'}</span>
                      </div>
                    </div>

                    {/* Confirmation checkbox */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      marginBottom: '16px',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        checked={cvConfirmed}
                        onChange={e => setCvConfirmed(e.target.checked)}
                        style={{ marginTop: '2px' }}
                      />
                      <span>I confirm the contact details on this CV match <strong>{item.name}</strong></span>
                    </label>

                    {/* Drop zone — only active when confirmed. Dropping without
                        confirming used to be silently discarded, which looked
                        exactly like a successful upload that vanished. */}
                    <div
                      onDragOver={e => { e.preventDefault(); if (cvConfirmed) setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={e => {
                        e.preventDefault();
                        setDragOver(false);
                        if (cvConfirmed) handleDrop(e);
                        else setError(`Tick the confirmation box above before uploading — the CV hasn't been sent.`);
                      }}
                      onClick={() => {
                        if (cvConfirmed) fileInputRef.current?.click();
                        else setError(`Tick the confirmation box above before uploading — the CV hasn't been sent.`);
                      }}
                      style={{
                        border: `2px dashed ${!cvConfirmed ? 'var(--border-subtle)' : dragOver ? 'var(--accent-teal)' : 'var(--border-card)'}`,
                        borderRadius: '12px',
                        padding: '32px',
                        textAlign: 'center',
                        cursor: cvConfirmed ? 'pointer' : 'not-allowed',
                        marginBottom: '16px',
                        background: dragOver ? 'rgba(93,143,154,0.05)' : 'transparent',
                        opacity: cvConfirmed ? 1 : 0.4,
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.rtf,.txt"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file && cvConfirmed) handleFileSelect(file);
                        }}
                      />
                      {fileName ? (
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>{fileName}</div>
                          <div style={{ fontSize: '12px', color: 'var(--accent-teal)' }}>Sending to DaXtra...</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📄</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>
                            {cvConfirmed ? 'Drop CV here' : 'Confirm details above first'}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>or click to browse — PDF, DOC, DOCX</div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {error && (
              <div className="warning-banner" style={{ marginBottom: '12px', fontSize: '12px' }}>{error}</div>
            )}

            {/* Submit button (not for upload-cv — that auto-submits on drop) */}
            {action !== 'upload-cv' && (
              <button className="btn btn-primary" onClick={handleSubmit} disabled={loading} style={{ width: '100%' }}>
                {loading ? 'Processing...' : (action === 'fix-currency'
                  ? (mismatchFix === 'location' ? 'Update location' : 'Update currency')
                  : actionButtonLabel(action))}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function actionTitle(action: string): string {
  const titles: Record<string, string> = {
    'log-call': 'Log Ad-Response Call',
    'close-vacancy': 'Close Vacancy',
    'fix-currency': 'Fix Currency Mismatch',
    'fix-address': 'Fix Address / Country',
    'add-tags': 'Add Source / Sector',
    'still-open': 'Acknowledge — Still Open',
    'correct-as-is': 'Acknowledge — Correct As-Is',
    'upload-cv': 'Upload CV',
    'fix-solution': 'Fix Solution',
    'edit-contact': 'Edit Contact Details',
    'dismiss': 'Dismiss (Admin)',
  };
  return titles[action] || action;
}

function actionButtonLabel(action: string): string {
  const labels: Record<string, string> = {
    'log-call': 'Log Call',
    'close-vacancy': 'Close Vacancy',
    'fix-currency': 'Update Currency',
    'fix-address': 'Update Country',
    'add-tags': 'Save',
    'still-open': 'Acknowledge',
    'correct-as-is': 'Acknowledge',
    'fix-solution': 'Update Solution',
    'edit-contact': 'Save Contact',
    'dismiss': 'Permanently Dismiss',
  };
  return labels[action] || 'Submit';
}
